// Garment library catalogue: slots, items (garmentCatalog.data.ts, generated
// from the Blender build's library.py), layering rules and asset paths.
// No runtime imports from extras.ts / catalog.ts (extras.ts imports this file).
import { LIB_ITEMS, LIB_FABRICS, LIB_PRINTS, LIB_GRAPHICS } from './garmentCatalog.data';

export { LIB_ITEMS, LIB_FABRICS, LIB_PRINTS, LIB_GRAPHICS };

/** Layering slots, innermost first. One item per slot. */
export const LIB_SLOTS = ['legwear', 'shoes', 'bottom', 'top', 'acc', 'outer', 'waist', 'head', 'hair'] as const;
export type LibSlot = (typeof LIB_SLOTS)[number];
export const LIB_SLOT_LABELS: Record<LibSlot, string> = {
  legwear: 'Socks & tights', shoes: 'Shoes', bottom: 'Bottoms', top: 'Tops', acc: 'Harness & chains', outer: 'Jackets & coats', waist: 'Tied at the waist', head: 'Hats', hair: 'Sculpted hair',
};

export const LIB_CHANNELS = ['main', 'accent', 'trim', 'metal', 'sole', 'lace', 'lining', 'print2'] as const;
export type LibChannel = (typeof LIB_CHANNELS)[number];
export const LIB_CHANNEL_LABELS: Record<LibChannel, string> = {
  main: 'Main', accent: 'Accent', trim: 'Trim', metal: 'Hardware', sole: 'Sole', lace: 'Laces / cords', lining: 'Lining', print2: 'Print',
};

export interface LibItem {
  id: string;
  label: string;
  slot: LibSlot;
  category: string;
  tags: string;
  /** default channel colours (only the channels the item uses) */
  colors: Partial<Record<LibChannel, string>>;
  /** body regions it covers (informational; exact vertex lists live in each part file) */
  hides: string[];
  fabric?: string;
  /** 'pat:<id>' = a builder v1 print (pat/<id>.jpg), else garments/print/<id>.jpg */
  print?: string;
  fabricScale?: number;
  printScale?: number;
  /** channel the detail texture's contrast stitching mixes toward (default trim) */
  mix?: LibChannel;
  /** wide hems that go over boots instead of being tucked in */
  overBoots?: boolean;
  /** items that can't be worn together with this one */
  excludes?: string[];
  /** has a front graphic (user may swap it) */
  graphic?: boolean;
  graphicDefault?: string;
  /** swap the decal graphic with pick.print */
  graphicSwap?: boolean;
  outline?: number;
}

export interface LibPick { id: string; colors?: Partial<Record<LibChannel, string>>; print?: string; fabric?: string }
export type LibSelection = Partial<Record<LibSlot, LibPick>>;

export const LIB_ROOT = 'garments/';
const BY_ID = new Map(LIB_ITEMS.map((i) => [i.id, i]));
export const libItem = (id: string | undefined) => (id ? BY_ID.get(id) : undefined);
export const libItemsFor = (slot: LibSlot) => LIB_ITEMS.filter((i) => i.slot === slot);
/** Part file for an item on a base + named body shape. */
export const libFile = (item: LibItem, base: 'a' | 'b', shape: string) => `${LIB_ROOT}parts/${base}-${shape}/${item.id}.glb`;
export const libThumb = (id: string) => `${LIB_ROOT}thumbs/${id}.webp`;
export const libFabricAsset = (id: string) => `${LIB_ROOT}fabric/${id}.jpg`;
export const libPrintAsset = (id: string) => (id.startsWith('pat:') ? `pat/${id.slice(4)}.jpg` : `${LIB_ROOT}print/${id}.jpg`);
export const libGraphicAsset = (id: string) => `${LIB_ROOT}graphic/${id}.png`;

/** Hood-up hoodies take the head slot (no hat over a hood). */
export const occupiesHead = (it: LibItem) => /hood up/i.test(it.label + ' ' + it.tags);
/** Open-front jackets need something underneath. */
export const OPEN_FRONT = new Set(['crop-moto', 'crop-pastel', 'jacket-denim', 'blazer', 'blazer-oversized', 'blazer-tux', 'coat-trench', 'coat-long', 'coat-duster']);
/** What the library fills in when a slot that must be covered is empty. */
export const LIB_FILL = { top: 'tee-crew', underOpen: 'tank', bottom: 'jeans-wide' } as const;

const HEX = /^#[0-9a-f]{6}$/i;

/**
 * Repair a library selection: known ids in their own slot, valid colours /
 * prints / fabrics, no excluded pairs, a hood-up hoodie clears the hat, and
 * an outfit that has any library piece always covers the torso and hips.
 */
export function normalizeLibrary(raw: unknown): LibSelection {
  const out: LibSelection = {};
  if (!raw || typeof raw !== 'object') return out;
  for (const slot of LIB_SLOTS) {
    const p = (raw as any)[slot];
    const it = libItem(typeof p === 'string' ? p : p?.id);
    if (!it || it.slot !== slot) continue;
    const pick: LibPick = { id: it.id };
    if (p && typeof p === 'object') {
      if (p.colors && typeof p.colors === 'object') {
        const c: Partial<Record<LibChannel, string>> = {};
        for (const ch of LIB_CHANNELS) if (typeof p.colors[ch] === 'string' && HEX.test(p.colors[ch])) c[ch] = p.colors[ch].toLowerCase();
        if (Object.keys(c).length) pick.colors = c;
      }
      if (typeof p.print === 'string' && (p.print === 'none' || LIB_PRINTS.includes(p.print) || /^pat:[a-z-]+$/.test(p.print) || LIB_GRAPHICS.includes(p.print))) pick.print = p.print;
      if (typeof p.fabric === 'string' && (p.fabric === 'none' || LIB_FABRICS.includes(p.fabric))) pick.fabric = p.fabric;
    }
    out[slot] = pick;
  }
  // exclusions (the later slot gives way)
  for (const slot of [...LIB_SLOTS].reverse()) {
    const it = libItem(out[slot]?.id);
    if (!it) continue;
    for (const other of LIB_SLOTS) {
      const o = libItem(out[other]?.id);
      if (o && o !== it && (it.excludes?.includes(o.id) || o.excludes?.includes(it.id))) delete out[slot];
    }
  }
  if (out.outer && occupiesHead(libItem(out.outer.id)!)) delete out.head;
  if (Object.keys(out).some((k) => k !== 'hair')) {
    if (!out.top && (!out.outer || OPEN_FRONT.has(out.outer.id))) out.top = { id: out.outer ? LIB_FILL.underOpen : LIB_FILL.top };
    if (!out.bottom) out.bottom = { id: LIB_FILL.bottom };
  }
  return out;
}

/** Does the selection wear library clothes (sculpted hair alone keeps the base outfit)? */
export const hasLibrary = (s: LibSelection | undefined) => !!s && Object.keys(s).some((k) => k !== 'hair');
export const hasLibraryAny = (s: LibSelection | undefined) => !!s && Object.keys(s).length > 0;

/** Nearest named body shape (garments are built per preset; sliders in between snap to the closest one and are blended by a residual shape field). */
export function libShapeFor(body: Record<string, number>, shapes: { id: string; body: Record<string, number> }[]): string {
  let best = shapes[0]?.id ?? 'base', bd = Infinity;
  for (const s of shapes) {
    let d = 0;
    for (const k of Object.keys(s.body)) d += ((body[k] ?? 0) - s.body[k]) ** 2;
    if (d < bd) { bd = d; best = s.id; }
  }
  return best;
}
